import React, { memo, useCallback, useEffect, useState } from 'react';
import Minimap from '../Minimap';
import FlowchartHUD from '../FlowchartHUD';
import FocusModeHUD from '../FocusModeHUD';
import FlowchartHints from '../FlowchartHints';
import NestModal from '../NestModal';
import CampaignLegend from '../CampaignLegend';
import MilestonePathModal from '../MilestonePathModal';
import FokusOverlay from '../../../shared/FokusOverlay';
import { findPreferredStatus } from '../../utils/statusUtils';
import { MILESTONE_PATH_OPEN_EVENT } from '../../utils/openMilestonePathModal';

/**
 * FlowchartOverlays Component
 * 
 * Renders all overlay elements: HUDs, minimap, sidebars, and modals.
 * Extracted from FlowchartView to reduce file size.
 */
const FlowchartOverlays = ({
  // Sprint info
  sprintName,
  sprintIcon,
  sprintProgress,
  breadcrumbPath,
  campaignId,
  
  // Tasks & state
  tasks,
  displayTasks,
  displayPositions,
  displayEdges,
  nodeDimensions,
  statuses,
  lockStates,
  nestedSprints,
  allSprints = [],
  allCategories,
  allTags = [],
  
  // Viewport
  pan,
  zoom,
  containerRef,
  contentBounds,
  focusContentBounds,
  
  // Selection
  selectedEdge,
  setSelectedNodes,
  
  // Focus mode
  isFocusMode,
  focusedTaskIds,
  allFocusedTaskIds,
  isAnimatingIntoFocus,
  isAnimatingOutOfFocus,
  handleExitFocusMode,
  isRestoringFokusState = false, // Skip animation when restoring after tab switch
  
  // Interaction state
  isPanning,
  isDragging,
  isConnecting,
  autoArrangeBlocked,
  
  // Nest action
  pendingNestAction,
  confirmNestAction,
  cancelNestAction,
  
  // Detail sidebar (now handled at SprintDetail level)
  onOpenDetails,
  
  // Handlers
  centerOnTask,
  setPan,
  setPanAnimated,
  constrainPan,
  onNavigateToParent,
  onSelectSprint,
  onRenameSprint,
  onSprintIconChange,
  onStatusChange,
  onCategoryChange,
  onNestTasks,
  // Multi-campaign mode
  isMultiCampaignMode = false,
  activeView = null,
  multiCampaignCampaigns = [],
  onViewRename = null,
  onViewIconChange = null,
  // HUD visibility settings
  flowViewVisibility = {},
  onFlowViewVisibilityChange,
  hideViewExplainerModal = false,
  onHideViewExplainerModalChange,
  // Mission hover state (for bidirectional highlighting)
  hoveredMissionId = null,
  onMissionHover,
  onMissionLeave,
}) => {
  const [milestonePathId, setMilestonePathId] = useState(null);

  useEffect(() => {
    const handleOpen = (e) => {
      setMilestonePathId(e.detail?.milestoneId ?? null);
    };
    window.addEventListener(MILESTONE_PATH_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(MILESTONE_PATH_OPEN_EVENT, handleOpen);
  }, []);

  const milestoneForPath = milestonePathId
    ? tasks.find((t) => t.id === milestonePathId)
    : null;

  const handleGoToTaskFromPath = useCallback((taskId) => {
    centerOnTask(taskId);
    setSelectedNodes(new Set([taskId]));
    setMilestonePathId(null);
  }, [centerOnTask, setSelectedNodes]);

  // Memoized callback for minimap size changes
  const handleMinimapSizeChange = useCallback((newSize) => {
    if (onFlowViewVisibilityChange) {
      onFlowViewVisibilityChange({ ...flowViewVisibility, minimapSize: newSize });
    }
  }, [onFlowViewVisibilityChange, flowViewVisibility]);

  // Handler to pan to a specific campaign's zone
  const handlePanToCampaign = useCallback((campaignId) => {
    if (!displayPositions || !containerRef?.current) return;
    
    // Find all tasks belonging to this campaign
    const campaignTasks = tasks.filter(t => t.sourceCampaignId === campaignId);
    if (campaignTasks.length === 0) return;
    
    // Calculate bounds for this campaign's tasks
    let minX = Infinity, minY = Infinity;
    
    campaignTasks.forEach(task => {
      const pos = displayPositions[task.id];
      if (pos) {
        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
      }
    });
    
    if (minX === Infinity) return;
    
    // Add padding (50px from edge of viewport)
    const padding = 50;
    
    // Calculate the pan needed to position the top-left of the campaign at the top-left of the viewport (with padding)
    const targetPan = {
      x: -minX * zoom + padding,
      y: -minY * zoom + padding,
    };
    
    setPanAnimated(targetPan);
  }, [tasks, displayPositions, containerRef, zoom, setPanAnimated]);

  return (
    <>
      {/* Focus mode overlays - shared dithered blue vignette */}
      {(isFocusMode || isAnimatingOutOfFocus) && (
        <FokusOverlay isFadingOut={isAnimatingOutOfFocus} skipEnterAnimation={isRestoringFokusState} />
      )}
      
      {/* Focus mode HUD */}
      <FocusModeHUD
        campaignName={sprintName}
        progress={sprintProgress}
        focusedCount={focusedTaskIds?.size || 0}
        totalVisibleCount={allFocusedTaskIds?.size || 0}
        onExitFocus={handleExitFocusMode}
        isVisible={isFocusMode && !isAnimatingOutOfFocus}
        isEntering={isAnimatingIntoFocus}
        isExiting={isAnimatingOutOfFocus}
      />

      <FlowchartHints
        isConnecting={isConnecting}
        selectedEdge={selectedEdge}
        autoArrangeBlocked={autoArrangeBlocked}
      />

      <FlowchartHUD
        sprintName={sprintName}
        sprintIcon={sprintIcon}
        progress={sprintProgress}
        tasks={tasks}
        statuses={statuses}
        lockStates={lockStates}
        nestedSprints={nestedSprints}
        allSprints={allSprints}
        isHidden={isFocusMode || isAnimatingIntoFocus}
        onGoToTask={(taskId) => {
          centerOnTask(taskId);
          setSelectedNodes(new Set([taskId]));
        }}
        onStartTask={(taskId) => {
          // Find the preferred "in_progress" status for this campaign
          const preferredStatus = findPreferredStatus(statuses, 'in_progress');
          onStatusChange?.(taskId, preferredStatus?.id || 'in_progress');
        }}
        onCompleteTask={(taskId) => {
          // Find the preferred "completed" status for this campaign
          const preferredStatus = findPreferredStatus(statuses, 'completed');
          onStatusChange?.(taskId, preferredStatus?.id || 'completed');
        }}
        breadcrumbPath={breadcrumbPath}
        onNavigateToParent={onNavigateToParent}
        onSelectSprint={onSelectSprint}
        onRename={onRenameSprint}
        onIconChange={onSprintIconChange}
        campaignId={campaignId}
        isMultiCampaignMode={isMultiCampaignMode}
        activeView={activeView}
        onViewRename={onViewRename}
        onViewIconChange={onViewIconChange}
        flowViewVisibility={flowViewVisibility}
        onFlowViewVisibilityChange={onFlowViewVisibilityChange}
        hideViewExplainerModal={hideViewExplainerModal}
        onHideViewExplainerModalChange={onHideViewExplainerModalChange}
        onMissionHover={onMissionHover}
        onMissionLeave={onMissionLeave}
      />

      {/* Minimap + Campaign Legend wrapper */}
      <div className="flowchart-minimap-wrapper">
        {/* Campaign Legend (multi-campaign mode) - positioned above minimap */}
        {isMultiCampaignMode && flowViewVisibility.campaignsKey !== false && (
          <CampaignLegend
            campaigns={multiCampaignCampaigns}
            viewName={activeView?.name || ''}
            isVisible={!isFocusMode}
            onCampaignClick={handlePanToCampaign}
          />
        )}
        
        {/* Minimap */}
        {flowViewVisibility.minimap !== false && (
          <Minimap
            key={campaignId ?? 'no-campaign'}
            campaignId={campaignId}
            tasks={displayTasks}
            nodePositions={displayPositions}
            nodeDimensions={nodeDimensions}
            edges={displayEdges}
            pan={pan}
            zoom={zoom}
            containerRef={containerRef}
            contentBounds={isFocusMode ? focusContentBounds : contentBounds}
            onPanChange={setPan}
            onPanChangeAnimated={setPanAnimated}
            constrainPan={constrainPan}
            allCategories={allCategories}
            allTags={allTags}
            lockStates={lockStates}
            statuses={statuses}
            nestedSprints={nestedSprints}
            minimapSize={flowViewVisibility.minimapSize}
            onMinimapSizeChange={onFlowViewVisibilityChange ? handleMinimapSizeChange : undefined}
          />
        )}
      </div>

      {/* Task Detail Sidebar is now rendered at SprintDetail level */}

      {/* Nest Modal */}
      <NestModal
        isOpen={!!pendingNestAction}
        targetTask={pendingNestAction?.targetTask}
        draggedIds={pendingNestAction?.draggedIds || []}
        dependencyInfo={pendingNestAction?.dependencyInfo}
        hasDependencies={pendingNestAction?.hasDependencies}
        tasks={tasks}
        onConfirm={(strategy, rememberChoice) => {
          const result = confirmNestAction(strategy);
          if (result && onNestTasks) {
            onNestTasks(result);
          }
        }}
        onCancel={cancelNestAction}
      />

      <MilestonePathModal
        milestone={milestoneForPath}
        tasks={tasks}
        statuses={statuses}
        nestedSprints={nestedSprints}
        allSprints={allSprints}
        isOpen={!!milestoneForPath}
        onClose={() => setMilestonePathId(null)}
        onGoToTask={handleGoToTaskFromPath}
      />
    </>
  );
};

// PERFORMANCE: Custom comparison to skip pan/zoom changes
// The Minimap handles pan/zoom updates via refs and custom events, not React re-renders
function areOverlaysPropsEqual(prevProps, nextProps) {
  // Skip pan and zoom - these are handled via refs/events in Minimap
  const { pan: prevPan, zoom: prevZoom, ...prevRest } = prevProps;
  const { pan: nextPan, zoom: nextZoom, ...nextRest } = nextProps;
  
  // Shallow compare all other props
  const prevKeys = Object.keys(prevRest);
  const nextKeys = Object.keys(nextRest);
  
  if (prevKeys.length !== nextKeys.length) return false;
  
  for (const key of prevKeys) {
    if (prevRest[key] !== nextRest[key]) return false;
  }
  
  return true;
}

export default memo(FlowchartOverlays, areOverlaysPropsEqual);

